import React from 'react';
import { Shield, Mail, Calendar, User, Database, Lock, Eye, Settings } from 'lucide-react';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Shield className="w-16 h-16 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-lg text-gray-600">Last updated: August 23, 2025</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8 space-y-8">
          
          {/* Who we are */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <User className="w-6 h-6 mr-3 text-blue-600" />
              Who we are
            </h2>
            <p className="text-gray-700 mb-4">
              KCI LLC ("Company", "we", "us", or "our") operates MyTrailer and the website kcirodeo.net (collectively, the "Services"). 
              We are the controller of personal information processed through the Services, except where we act as a processor for equine 
              associations or other partners who provide event and results data.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
              <p className="text-blue-800">
                <strong>Contact us:</strong> <a href="mailto:kcirodeo@outlook.com" className="text-blue-600 hover:text-blue-800 underline">kcirodeo@outlook.com</a>
              </p>
            </div>
          </section>

          {/* What this policy covers */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Eye className="w-6 h-6 mr-3 text-blue-600" />
              What this policy covers
            </h2>
            <p className="text-gray-700 mb-4">
              This policy explains what personal information we collect, how we use it, the choices and rights you have, and how to contact us. It applies to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Visitors to our website(s)</li>
              <li>Users who create accounts on MyTrailer</li>
              <li>People who communicate with us or other users via our in‑app messaging features</li>
            </ul>
            <p className="text-gray-700 mt-4">
              This policy does not cover third‑party websites, apps, or services that we do not control (including association sites or payment providers). 
              Their privacy practices are governed by their own policies.
            </p>
          </section>

          {/* Information we collect */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Database className="w-6 h-6 mr-3 text-blue-600" />
              Information we collect
            </h2>
            <p className="text-gray-700 mb-4">
              The information we collect depends on how you use the Services. We collect information directly from you, automatically, and from third parties (e.g., equine associations you choose to link).
            </p>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-medium text-gray-900 mb-3">1) Information you provide to us</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong>Account & profile data:</strong> name, display name, email, password (hashed), phone number (optional), profile photo, timezone, communication preferences.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong>Horse profiles:</strong> horse name, breed, sex, date of birth/age, markings, registration numbers, vaccination or care history (note: this describes animal data; do not upload human medical data), ownership details you choose to store.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong>Event & results data you submit:</strong> entries, performance results, placements, points, photos/videos you upload, comments/notes.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong>Messages & social features:</strong> messages you send/receive, posts, reactions, invites, and user‑generated content (UGC). We process this to deliver communications and to enforce our policies.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong>Support requests & surveys:</strong> contact details and the content of your requests, feedback, or survey responses.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong>Payment information:</strong> When you purchase subscriptions or services, payments are processed by our provider (e.g., Stripe). We receive limited payment metadata (e.g., last4, card type, billing country) and transaction status; we do not store full card numbers.
                    </div>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-900 mb-3">2) Information collected automatically</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong>Usage & device data:</strong> app version, browser type, OS, device identifiers, language, referring/exit pages, pages viewed, feature usage, timestamps.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong>Approximate location:</strong> derived from IP address to provide time‑zone, localization, fraud prevention, or analytics.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong>Cookies & similar technologies:</strong> pixels and SDKs for essential operations, analytics, and (if enabled) advertising. See Cookies below.
                    </div>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-900 mb-3">3) Information from third parties</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong>Association/partner data you connect:</strong> event calendars, registrations, draws, and official results about you or your horses, provided by associations you choose to link.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong>Single sign‑on (SSO) or social login:</strong> name, email, and identifiers as permitted by your settings.
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg mt-4">
              <p className="text-yellow-800">
                <strong>Important:</strong> We do not intentionally collect sensitive personal information about individuals (e.g., government IDs, precise geolocation, biometric data), and you should not upload such information to the Services.
              </p>
            </div>
          </section>

          {/* How we use information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Settings className="w-6 h-6 mr-3 text-blue-600" />
              How we use information
            </h2>
            <p className="text-gray-700 mb-4">We use personal information to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Provide the Services (account creation, authentication, horse records, event schedules, results, messaging, notifications).</li>
              <li>Maintain safety (monitoring and enforcing acceptable use, investigating abuse, spam, or fraud, and protecting the rights, property, and safety of users).</li>
              <li>Improve and personalize (debugging, analytics, research, and developing new features; tailoring content and recommendations).</li>
              <li>Communicate with you (transactional emails, support responses, policy updates, and—if you opt‑in—marketing communications).</li>
              <li>Comply with legal obligations (tax, accounting, regulatory requests; responding to lawful requests from authorities).</li>
              <li>Process payments and manage subscriptions via our payment provider.</li>
            </ul>
          </section>

          {/* Messaging and user-generated content */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Messaging and user‑generated content</h2>
            <p className="text-gray-700 mb-4">
              Messages are delivered and stored so that you and intended recipients can access them. We do not review message content except:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>As necessary to operate the Services (e.g., deliver messages, prevent spam or abuse),</li>
              <li>When flagged by users or automated systems for policy violations, or</li>
              <li>When required by law.</li>
            </ul>
            <p className="text-gray-700 mt-4">
              We may retain metadata (timestamps, participants) for security and abuse prevention.
            </p>
          </section>

          {/* Sharing of information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Sharing of information</h2>
            <p className="text-gray-700 mb-4">We share information only as described below:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Service providers/Processors:</strong> hosting, storage, analytics, messaging, content delivery, email, customer support, payment processing, and security vendors working on our behalf under contract.</li>
              <li><strong>Association partners you connect:</strong> if you choose to link an association, we may receive and/or share event and results data as needed to provide the features you use.</li>
              <li><strong>Legal and safety:</strong> to comply with law or enforce our terms; to investigate or prevent fraud, abuse, or security incidents.</li>
              <li><strong>Business transfers:</strong> as part of a merger, acquisition, financing, or sale of assets (we'll continue to protect your data and give notice of any changes).</li>
            </ul>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg mt-4">
              <p className="text-blue-800">
                <strong>Important:</strong> We do not sell personal information. We may engage in "sharing" or "targeted advertising" as defined by U.S. state privacy laws only if you opt‑in via our cookie/banner preferences. You can opt‑out at any time (see Your Privacy Rights).
              </p>
            </div>
          </section>

          {/* Retention */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Retention</h2>
            <p className="text-gray-700 mb-4">
              We keep personal information only as long as necessary to provide the Services, comply with legal obligations, resolve disputes, and enforce agreements. Typical retention:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Account data:</strong> kept while your account is active; deleted or anonymized within 30 days after deletion request, subject to legal holds.</li>
              <li><strong>Messages/UGC:</strong> retained until you delete them or your account is deleted, unless required for security, abuse prevention, or legal obligations.</li>
              <li><strong>Logs/analytics:</strong> kept for up to 13 months (or shorter where required).</li>
            </ul>
          </section>

          {/* Your choices */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your choices</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Profile & account settings:</strong> update your info, control what is visible to others.</li>
              <li><strong>Communications:</strong> manage email and push notification preferences in Settings; you can opt‑out of marketing at any time.</li>
              <li><strong>Cookies:</strong> manage preferences via our Cookie Settings banner and your browser settings.</li>
              <li><strong>Do Not Track:</strong> we do not respond to browser DNT signals; use our in‑app and cookie controls.</li>
            </ul>
          </section>

          {/* Your privacy rights */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your privacy rights</h2>
            <p className="text-gray-700 mb-4">
              Your rights depend on where you live and may include the right to access, correct, delete, port, or restrict processing of your personal information, and to opt‑out of targeted advertising, the sale or sharing of personal information, and certain profiling.
            </p>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-3">United States (State laws such as CA/CO/CT/VA/UT/TX/OR/MT/IA and others)</h3>
              <p className="text-gray-700 mb-3">
                Depending on your state, you may have rights to: know/access, correct, delete, data portability, and to opt‑out of (a) selling or sharing personal information, (b) targeted advertising, and (c) profiling with legal/effects. For California residents, we also disclose the categories of personal information collected, sources, purposes, and how long we keep each category (see California Notice below).
              </p>
              <p className="text-gray-700 mb-3">
                <strong>Exercising your rights:</strong> Submit a request at <a href="mailto:kcirodeo@outlook.com" className="text-blue-600 hover:text-blue-800 underline">kcirodeo@outlook.com</a>. We may verify your identity and respond within the timeframe required by law.
              </p>
              <p className="text-gray-700">
                <strong>Appeal process:</strong> If we deny your request, you may appeal by emailing <a href="mailto:kcirodeo@outlook.com" className="text-blue-600 hover:text-blue-800 underline">kcirodeo@outlook.com</a> with "Appeal" in the subject line.
              </p>
            </div>
          </section>

          {/* Security */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Lock className="w-6 h-6 mr-3 text-blue-600" />
              Security
            </h2>
            <p className="text-gray-700">
              We use administrative, technical, and physical safeguards appropriate to the nature of the data (e.g., encryption in transit, access controls, logging, and periodic reviews). No system is 100% secure; please use a strong, unique password and keep it confidential.
            </p>
          </section>

          {/* Children's privacy */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Children's privacy</h2>
            <p className="text-gray-700">
              The Services are not directed to children under 13 (or the minimum age required by your jurisdiction). We do not knowingly collect personal information from children. If you believe a child has provided personal information, contact us to request deletion.
            </p>
          </section>

          {/* Cookies and similar technologies */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cookies and similar technologies</h2>
            <p className="text-gray-700 mb-4">We use:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Essential cookies for core functionality (authentication, security, load balancing).</li>
              <li>Analytics cookies to understand usage and improve the Services.</li>
              <li>Advertising/measurement technologies only if you opt‑in.</li>
            </ul>
            <p className="text-gray-700 mt-4">
              You can manage preferences via Cookie Settings and your browser/device settings. Disabling some cookies may affect functionality.
            </p>
          </section>

          {/* California notice */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">California notice (CCPA/CPRA)</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Categories collected:</h3>
                <p className="text-gray-700">
                  identifiers (e.g., name, email), commercial information (transactions), internet/usage data, general geolocation (coarse IP‑based), in‑app communications metadata, and inferences for personalization. Sensitive personal information: we do not use or disclose SPI for purposes requiring a Right to Limit.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Sources:</h3>
                <p className="text-gray-700">you, your devices, partners you connect (e.g., equine associations), and service providers.</p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Business purposes:</h3>
                <p className="text-gray-700">as described in How we use information (providing services, security/fraud prevention, improvement, communications, compliance).</p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Disclosures:</h3>
                <p className="text-gray-700">to service providers, processors, and partners as outlined above. We do not sell personal information. We may share personal information for cross‑context behavioral advertising if you opt‑in; you can opt‑out at any time via [Do Not Sell/Share link] or Cookie Settings.</p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Retention:</h3>
                <p className="text-gray-700">see Retention above.</p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Your rights:</h3>
                <p className="text-gray-700">access, correction, deletion, portability, and the rights to opt‑out of selling/sharing and targeted advertising. We will not discriminate against you for exercising your rights.</p>
              </div>
            </div>
          </section>

          {/* Third-party links & services */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third‑party links & services</h2>
            <p className="text-gray-700">
              The Services may contain links to third‑party sites (e.g., association pages, payment providers). We are not responsible for their privacy practices.
            </p>
          </section>

          {/* Changes to this policy */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to this policy</h2>
            <p className="text-gray-700">
              We may update this policy from time to time. If we make material changes, we will post the updated policy and update the "Last updated" date. If required by law, we will notify you and/or request consent.
            </p>
          </section>

          {/* How to contact us */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Mail className="w-6 h-6 mr-3 text-blue-600" />
              How to contact us
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
              <p className="text-blue-800">
                Questions or requests? Email <a href="mailto:kcirodeo@outlook.com" className="text-blue-600 hover:text-blue-800 underline font-medium">kcirodeo@outlook.com</a>
              </p>
            </div>
          </section>
        </div>

        {/* Back to top button */}
        <div className="text-center mt-8">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Shield className="w-5 h-5 mr-2" />
            Back to Top
          </button>
        </div>
      </div>
    </div>
  );
}
